import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR, formatIST } from "@/lib/format";
import { PageHeader } from "@/components/Shell";
import { DisputeBadge, isDisputeClosed } from "@/components/DisputeStatus";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, ShieldAlert } from "lucide-react";

export default function UserDisputes() {
  const [list, setList] = useState(null);
  const load = useCallback(() => { api.get("/user/disputes").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  useEffect(() => { load(); }, [load]);

  if (!list) return <div className="grid place-items-center py-32"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div>;

  return (
    <div>
      <PageHeader title="Disputes" desc="Chargebacks or disputes raised on your transactions." />
      {list.length === 0 ? (
        <div className="vp-card rounded-xl p-10 text-center text-slate-500" data-testid="no-disputes">
          <ShieldAlert className="mx-auto mb-3 h-8 w-8 text-slate-600" /> No disputes on your account.
        </div>
      ) : (
        <div className="space-y-4">{list.map((d) => <DisputeCard key={d.id} d={d} onChanged={load} />)}</div>
      )}
    </div>
  );
}

function DisputeCard({ d, onChanged }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const respond = async () => {
    setBusy(true);
    try { await api.post(`/user/disputes/${d.dispute_id}/response`, { text }); toast.success("Response submitted"); setText(""); onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const updates = d.history || [];

  return (
    <div className="vp-card rounded-xl p-5" data-testid={`user-dispute-${d.dispute_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-mono text-sm text-slate-400">{d.dispute_id}</div>
          <div className="mt-1 font-display text-2xl font-bold text-rose-300">{formatINR(d.disputed_amount)}</div>
          <div className="text-xs text-slate-500 font-mono">Original txn: {d.txn_id} · {formatIST(d.created_at)}</div>
        </div>
        <DisputeBadge status={d.status} />
      </div>
      <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
        <Info label="Reason" value={d.reason} />
        <Info label="Amount on hold" value={formatINR(d.hold_amount)} />
        <Info label="Amount reversed" value={formatINR(d.reversed_amount)} />
      </div>
      {d.user_message && <div className="mt-3 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-sm text-amber-200" data-testid="dispute-user-message">{d.user_message}</div>}
      <div className="mt-4 space-y-1.5">
        {updates.map((h, i) => (
          <div key={i} className="text-xs text-slate-400 font-mono"><span className="text-slate-600">{formatIST(h.at)}</span> · {h.kind === "status" ? `Status: ${h.text.replace("_", " ")}` : h.text}</div>
        ))}
        {(d.user_responses || []).map((r, i) => (
          <div key={`r${i}`} className="text-xs text-cyan-300 font-mono"><span className="text-slate-600">{formatIST(r.at)}</span> · You: {r.text}</div>
        ))}
      </div>
      {!isDisputeClosed(d.status) && (
        <div className="mt-4 space-y-2">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Add your response or evidence details (UTR, screenshots description, etc.)" data-testid={`dispute-response-${d.dispute_id}`} className="bg-slate-900/60 border-slate-700" />
          <Button onClick={respond} disabled={busy || !text.trim()} data-testid={`dispute-respond-btn-${d.dispute_id}`} className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold">Submit Response</Button>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return <div className="rounded-lg bg-black/20 border border-white/5 p-3"><div className="text-xs text-slate-500">{label}</div><div className="mt-0.5">{value}</div></div>;
}
